import { Injectable } from '@angular/core';
import { fromJS, List } from 'immutable';
import { has, isBoolean, keys, reduce } from 'lodash';
import {
  ALL_LIST_FILTER,
  IFetchFilterKey,
  ISearchParams,
  SearchParamsEnum,
  SortByEnum,
  SortOrderEnum,
} from '../entity-data/entity-data.data';
import {
  getInContent,
  IS_DIRTY_KEY,
  IS_NEW_KEY,
  setInContent,
} from '../entity-data/entity.model';
import { Settings } from '../settings-data/settings.model';
import { Client } from './client.model';
import { EntityDataBuilder } from '../entity-data/entity-data.builder';
import { EntityTypesEnum } from '../../app.enum';
import { CLIENT_LIST_SEARCH_PARAMS_KEY } from './client-data.data';
import { UrlService } from '../shared/services/url/url.service';
import { AccountQuotas } from '../features-data/sub-models/account-quotas.model';
import { DateTimeService, MOMENT_TIMEZONELESS_DATE_FORMAT } from 'i2g-ng-utils';
import { Document } from '../document-data/document.model';
import { EmailService } from '../shared/services/email/email.service';

@Injectable()
export class ClientDataBuilder extends EntityDataBuilder<Client> {
  model = Client;
  entityType: EntityTypesEnum = EntityTypesEnum.client;
  quotaName = AccountQuotas.KEYS.CLIENTS;
  listFilterKeys: IFetchFilterKey = {
    [ALL_LIST_FILTER]: CLIENT_LIST_SEARCH_PARAMS_KEY,
  };

  constructor(
    private _dateTimeService: DateTimeService,
    private _emailService: EmailService,
    urlService: UrlService
  ) {
    super(urlService);
  }

  buildEntity(
    props: any = {},
    settings: Settings,
    isDirty?: boolean,
    isNew?: boolean
  ): Client {
    const localeKey = Client.CONTENT_KEYS.LOCALE;

    props = props && props.toMap ? props.toMap() : fromJS(props || {});

    const clientProps = props.withMutations((map) => {
      // Default locale
      if (!getInContent(map, localeKey)) {
        setInContent(map, localeKey, settings.documentLocale);
      }

      if (isBoolean(isDirty)) {
        map.set(IS_DIRTY_KEY, isDirty);
      }

      if (isBoolean(isNew)) {
        map.set(IS_NEW_KEY, isNew);
      }
    });

    return new Client(clientProps);
  }

  buildEntities(array) {
    return List<Client>(array ? array.map(this.buildEntity.bind(this)) : []);
  }

  buildSortItemsComparatorFn(
    sortBy: SortByEnum,
    sortOrder: SortOrderEnum,
    settings: Settings
  ): (clientA: Client, clientB: Client) => number {
    const order = sortOrder === SortOrderEnum.asc ? 1 : -1;
    const currencyCode: string = settings.documentCurrencyCode;

    const billingNameComparatorFn = (a: Client, b: Client) => {
      const billingNameA = (a.billingName || '').toLowerCase();
      const billingNameB = (b.billingName || '').toLowerCase();
      if (billingNameA > billingNameB) {
        return order;
      } else if (billingNameA < billingNameB) {
        return order * -1;
      } else {
        return 0;
      }
    };

    const emailComparatorFn = (a: Client, b: Client) => {
      const emailA = (a.email || '').toLowerCase();
      const emailB = (b.email || '').toLowerCase();
      if (emailA > emailB) {
        return order;
      } else if (emailA > emailB) {
        return order * -1;
      } else {
        return 0;
      }
    };

    const totalOwedComparatorFn = (a: Client, b: Client) => {
      const totalA = a.totals.find(
        (total) => total.currencyCode === currencyCode
      );
      const totalB = b.totals.find(
        (total) => total.currencyCode === currencyCode
      );
      const amountA = totalA ? totalA.owed : 0;
      const amountB = totalB ? totalB.owed : 0;
      if (amountA > amountB) {
        return order;
      } else if (amountA < amountB) {
        return order * -1;
      } else {
        return 0;
      }
    };

    return (a: Client, b: Client) => {
      if (sortBy === SortByEnum.billing_name) {
        return (
          billingNameComparatorFn(a, b) ||
          totalOwedComparatorFn(a, b) ||
          emailComparatorFn(a, b) ||
          0
        );
      } else if (sortBy === SortByEnum.total_owed) {
        return (
          totalOwedComparatorFn(a, b) ||
          billingNameComparatorFn(a, b) ||
          emailComparatorFn(a, b) ||
          0
        );
      }
    };
  }

  buildListItemsFilterFn(): (entity: any) => boolean {
    return (client: Client) => true;
  }

  buildListFilter(entity: any): string {
    return ALL_LIST_FILTER;
  }

  validateSearchParams(searchParams: ISearchParams, filter: string) {
    const validParams = {
      [SearchParamsEnum.sort_by]: (a) =>
        [SortByEnum.billing_name, SortByEnum.total_owed].includes(a),
      [SearchParamsEnum.sort_order]: (a) =>
        [SortOrderEnum.asc, SortOrderEnum.desc].includes(a),
    };
    const requiredKeys = keys(validParams).every((key) =>
      has(searchParams, key)
    );
    const validValues = reduce(
      searchParams,
      (result, value, key) =>
        result && has(validParams, key) && validParams[key](value),
      true
    );

    return requiredKeys && validValues;
  }

  buildUniquenessCheckSearchParams(client: Client): ISearchParams {
    const email = this._emailService.tokenizeEmails(client.email).join(' ');

    return {
      query: `${client.billingName || ''} ${email} ${client.contactName || ''}`,
    };
  }

  buildDocumentsComparatorFn(): (a: Document, b: Document) => number {
    const docNumberAscSort = (a: Document, b: Document): number => {
      const sortableNumberA = a.sortable.docNumber || '';
      const sortableNumberB = b.sortable.docNumber || '';

      if (sortableNumberA < sortableNumberB) {
        return -1;
      } else if (sortableNumberA > sortableNumberB) {
        return 1;
      } else {
        return 0;
      }
    };
    const docDateDescSort = (a: Document, b: Document): number => {
      const docDateA = this._dateTimeService.getMilliseconds(
        a.docDate,
        MOMENT_TIMEZONELESS_DATE_FORMAT
      );
      const docDateB = this._dateTimeService.getMilliseconds(
        b.docDate,
        MOMENT_TIMEZONELESS_DATE_FORMAT
      );

      if (docDateA < docDateB) {
        return 1;
      } else if (docDateA > docDateB) {
        return -1;
      } else {
        return docNumberAscSort(a, b); // secondary sort
      }
    };

    return docDateDescSort;
  }
}
